// @ts-nocheck
import { add, formatISO, isBefore, subDays } from "date-fns";
import { range as Rrange, tap, update as R_update } from "ramda";
import { combineLatest, EMPTY, forkJoin, Observable, of } from "rxjs";
import { catchError, concatMap, filter, first, map, switchMap } from "rxjs/operators";
import { defaultUserState } from "../../store/user/state";
import { dateTimeMidnight } from "../../utils/date";
import { CustomError } from "../../utils/error";
import { logDebug, logger } from "../../utils/logger";
import { reportAnalyticsEvent } from "../analytics/analytics.service";
import { authStateObservable, logOutObservable } from "../Auth/AuthService";
import {
  addItemToEntityObservable,
  addItemToEntitySubCollection,
  entityHasItem,
  entityItemByIdObservable,
  getEntityItem,
  getEntitySubCollectionItem,
  getSubCollectionObservable,
  subCollectionItemByIdObservable,
  subCollectionObservable,
  updateEntityItemObservable,
  updateEntitySubCollectionItemObservable,
} from "../Database/Database";
import { isAndroid } from "../Device/device";
import { NotificationsManager } from "../Device/notifications";
import { ChallengeAction } from "../Gamification/interfaces/Challenge.interface";
import { Level, LevelEntity } from "../Gamification/interfaces/Level.interface";
import {
  ActionStatus,
  FeedHistoryItem,
  GameUpdates,
  ScheduledAction,
  UserActionStats,
  UserChallenge,
  UserProfile,
  UserProgressUpdate,
  UserQuizz,
} from "./User.interface";

const PROFILES = "profiles";
const CHALLENGES = "challenges";
const FEED = "feed";
const QUIZZES = "quizzes";

function createNewProfile(
  userId: string,
  displayName: string,
  photoUrl: string,
  email: string,
  language: string
) {
  const defaultUserProfile: UserProfile = {
    status: "new",
    displayName: displayName,
    level: "NOVICE",
    photoURL: photoUrl,
    zenPoints: 0,
    email: email,
    tutorial: [],
    creationTime: new Date().getTime(),
    currentTimeOffset: new Date().getTimezoneOffset(),
    timeZoneOffset: new Date().getTimezoneOffset(),
    notifications: "request",
    unlocked: {
      locks: [],
      achievements: [],
      chapters: [],
      rewards: [],
    },
    stats: {
      actions: {
        count: 0,
        done: 0,
        skipped: 0,
        doneStreak: 0,
      },
      challenges: {
        count: 0,
        completed: 0,
        failed: 0,
        completedStreak: 0,
        failedStreak: 0,
      },
    },
    userId: userId,
    userLanguage: language,
    lastSync: new Date().getTime(),
    updatedAt: new Date().getTime(),
  };

  return addItemToEntityObservable(PROFILES, defaultUserProfile, userId).pipe(
    switchMap(() => entityItemByIdObservable<UserProfile>(PROFILES, userId))
  );
}

function getOrCreateUserProfile({
  user,
  language,
}: {
  user: {
    uid: string;
    displayName: string;
    photoURL: string;
    email: string;
  };
  language: string;
  initialLevel: Level;
}): Observable<UserProfile> {
  return entityHasItem(PROFILES, user.uid).pipe(
    switchMap((hasItem) => {
      if (hasItem) {
        return entityItemByIdObservable<UserProfile>(PROFILES, user.uid);
      } else {
        return createNewProfile(
          user.uid,
          user.displayName ?? '',
          user.photoURL ?? '',
          user.email,
          language
        );
      }
    })
  );
}

function updateUserProfile(userId: string, userProfile: Partial<UserProfile>) {
  return updateEntityItemObservable(PROFILES, userProfile, userId);
}

function deleteUserProfile(userId: string) {
  const userDelete: Pick<UserProfile, 'status'> = { status: 'deleted' };
  return updateEntityItemObservable(PROFILES, userDelete, userId).pipe(
    switchMap(() => logOutObservable())
  )
}

const updateScheduledAction = (
  actionDate: number,
  actionStatus: ActionStatus,
  scheduledActions: Array<ScheduledAction>
): Array<ScheduledAction> => {
  const actionIndex = scheduledActions.findIndex(
    (action) => action.actionDate === actionDate
  );
  return R_update(
    actionIndex,
    {
      ...scheduledActions[actionIndex],
      actionStatus: actionStatus,
      updatedAt: new Date().getTime(),
    },
    scheduledActions
  );
};

function updateActionStatus(
  userId: string,
  challengeId: string,
  actions: Array<{
    actionStatus: ActionStatus;
    actionDate: number;
  }>
) {
  const updateTime = new Date().getTime();
  return getUserChallenge(userId, challengeId).pipe(
    switchMap((userChallenge) => {
      const updatedScheduledActions = actions.reduce(
        (updated, action) =>
          updateScheduledAction(
            action.actionDate,
            action.actionStatus,
            updated
          ),
        userChallenge.scheduledActions
      );
      return updateEntitySubCollectionItemObservable<UserChallenge>(
        PROFILES,
        userId,
        CHALLENGES,
        {
          scheduledActions: updatedScheduledActions,
          updatedAt: updateTime,
        },
        challengeId
      );
    })
  );
}

function computeStats(
  actions: Array<{
    challengeId: string;
    toggled: boolean;
    actionDate: number;
  }>,
  previousStats: UserActionStats
): UserActionStats {
  const actionsSortedByDate = actions.sort(
    (a, b) => b.actionDate - a.actionDate
  );
  const latestFailedIndex = actionsSortedByDate.findIndex((c) => !c.toggled);
  const countFailed = actions.filter((ac) => !ac.toggled).length;
  const countDone = (actions.length = countFailed);
  return {
    done: previousStats.done + countDone,
    count: previousStats.count + actions.length,
    doneStreak:
      latestFailedIndex >= 0
        ? latestFailedIndex
        : previousStats.doneStreak + actions.length,
    skipped: previousStats.skipped + countFailed,
  };
}

function bulkUpdateActionStatus(
  userId: string,
  actions: Array<{
    challengeId: string;
    toggled: boolean;
    actionDate: number;
  }>
) {
  const actionsByChallenge = actions.reduce((actionsByChallenge, ac) => {
    const actionDetail: { actionDate: number; actionStatus: ActionStatus } = {
      actionStatus: ac.toggled ? "done" : "skipped",
      actionDate: ac.actionDate,
    };
    const found = actionsByChallenge[ac.challengeId];
    if (found) {
      return {
        ...actionsByChallenge,
        [ac.challengeId]: [...actionsByChallenge[ac.challengeId], actionDetail],
      };
    } else {
      return {
        ...actionsByChallenge,
        [ac.challengeId]: [actionDetail],
      };
    }
  }, {} as Record<string, Array<{ actionStatus: ActionStatus; actionDate: number }>>);

  const updateChallenges$ = Object.entries(actionsByChallenge).map(
    ([key, data]) => {
      return updateActionStatus(userId, key, data);
    }
  );

  return combineLatest(updateChallenges$).pipe(
    switchMap(() => {
      return getUserProfile(userId).pipe(
        switchMap((userProfile) => {
          const latestStats = userProfile.stats.actions;
          const newActionStats = computeStats(actions, latestStats);
          const updateTime = new Date().getTime();
          return updateUserProfile(userId, {
            stats: { ...userProfile.stats, actions: newActionStats },
            updatedAt: updateTime,
          });
        })
      );
    })
  );
}

function updateUserChallenge(
  userId: string,
  challengeId: string,
  userChallenge: Partial<UserChallenge>
) {
  return updateEntitySubCollectionItemObservable<UserChallenge>(
    PROFILES,
    userId,
    CHALLENGES,
    userChallenge,
    challengeId
  );
}

function addUserChallenge(userId: string, userChallenge: UserChallenge) {
  return addItemToEntitySubCollection<UserChallenge>(
    PROFILES,
    userId,
    CHALLENGES,
    userChallenge,
    userChallenge.challengeId
  );
}

function addUserQuizz(userId: string, userQuizz: UserQuizz) {
  return addItemToEntitySubCollection<UserQuizz>(
    PROFILES,
    userId,
    QUIZZES,
    userQuizz,
    userQuizz.quizzId
  );
}

function userProfileObservable(userId: string) {
  return entityItemByIdObservable<UserProfile>(PROFILES, userId);
}

function userAuthObservable(language: string) {
  const level$ = new LevelEntity()
    .getByName("NOVICE", { locale: language })
    .pipe(
      map((levels) => levels[0]),
      catchError(() => of(undefined))
    );

  return combineLatest([authStateObservable(), level$]).pipe(
    switchMap(([user, initialLevel]) => {
      if (user) {
        return getOrCreateUserProfile({
          user,
          initialLevel,
          language,
        }).pipe(
          switchMap((profile) => {
            return reportAnalyticsEvent<UserProfile>({
              event: "login",
              details: {
                platform: isAndroid() ? "android" : "ios",
                profileId: profile.userId,
                ...profile,
              },
            }).pipe(map(() => profile));
          })
        );
      } else {
        return of(defaultUserState);
      }
    })
  );
}

function _computeRepeat({
  duration,
  actionsCount,
  schedule,
}: {
  duration: number;
  actionsCount: number;
  schedule: number;
}) {
  if (duration === 0) return 1;
  const repeat = Math.floor((7 * duration) / schedule / actionsCount);
  if (schedule * actionsCount > 7 * schedule || (!repeat && actionsCount > 1)) {
    const actionsGenerateError = new CustomError(
      `Failed generating challenge scheduled actions, number of actions ${actionsCount}`,
      { name: "AppError" }
    );
    actionsGenerateError.context = `Duration:[${duration}] - Schedule:[${schedule}] - ActionsCount: [${actionsCount}]`;
    throw actionsGenerateError;
  }
  return repeat;
}

function generateActions({
  chapterId,
  challengeId,
  actions,
  startDate,
  duration,
  schedule,
}: {
  chapterId: string;
  challengeId: string;
  actions: Array<ChallengeAction>;
  startDate: number;
  duration: number;
  schedule: number;
}): Array<ScheduledAction> {
  logger.info(
    `Generate actions called with parameters: duration: ${duration}, schedule: ${schedule}`
  );
  const repeat = _computeRepeat({
    duration,
    actionsCount: actions.length,
    schedule,
  });
  return actions.reduce((scheduledActions, action, index) => {
    const refDate = add(
      new Date(formatISO(startDate, { representation: "date" })),
      {
        days: index * repeat * schedule,
      }
    );
    logger.info(
      `Generated challenge scheduled action: Challenge[${challengeId}] - action index: ${index} - Repeat: [${repeat}]- Action date: [${refDate.toISOString()}]`
    );
    return [
      ...scheduledActions,
      ...Rrange(0, repeat).map(
        (reapeatIndex) =>
        ({
          actionDate: add(refDate, {
            days: reapeatIndex * schedule,
          }).getTime(),
          actionId: action.actionId,
          actionStatus: "upcoming",
          challengeId: challengeId,
          chapterId: chapterId,
          updatedAt: new Date().getTime(),
        } as ScheduledAction)
      ),
    ];
  }, [] as Array<ScheduledAction>);
}

function userChallengeListObservable(userId: string) {
  return subCollectionObservable<UserChallenge>(PROFILES, userId, CHALLENGES);
}

function userChallengeObservable(userId: string, challengeId: string) {
  return subCollectionItemByIdObservable<UserChallenge>(
    PROFILES,
    userId,
    CHALLENGES,
    challengeId
  );
}

function userLatestUpdatedChallenges(
  userId: string,
  lastSync: number
): Observable<Array<UserChallenge>> {
  return getSubCollectionObservable<UserChallenge>(
    PROFILES,
    userId,
    CHALLENGES
  ).pipe(map((challenges) => challenges.filter((c) => c.updatedAt > lastSync)));
}

function userFeedObservable(userId: string) {
  return subCollectionObservable<FeedHistoryItem>(PROFILES, userId, FEED);
}

function updateUserFeedItemObservable(
  userId: string,
  feedItem: Partial<FeedHistoryItem>
) {
  return updateEntitySubCollectionItemObservable<FeedHistoryItem>(
    PROFILES,
    userId,
    FEED,
    feedItem,
    feedItem.itemId
  );
}

function getUserChallenge(userId: string, challengeId: string) {
  return getEntitySubCollectionItem<UserChallenge>(
    PROFILES,
    userId,
    CHALLENGES,
    challengeId
  );
}

function getUserProfile(userId: string) {
  return getEntityItem<UserProfile>(PROFILES, userId);
}

function userScoreUpdate(
  gameUpdates: GameUpdates,
  userProfile: UserProfile
): UserProgressUpdate {
  logDebug("user-service", "userScoreUpdate", "params", {
    gameUpdates,
    userProfile,
  });
  let updateSync = false;
  let nextUserProfile = { ...userProfile };

  if (gameUpdates?.score?.zenPoints) {
    nextUserProfile = {
      ...nextUserProfile,
      zenPoints: userProfile.zenPoints + gameUpdates.score.zenPoints,
    };
    updateSync = true;
  }
  const newProgress: UserProgressUpdate = {
    gameUpdates,
    userProfile: nextUserProfile,
    updateSync,
  };
  logDebug("user-service", "userScoreUpdate", "return", { newProgress });
  return newProgress;
}

const challengeProgressUpdate = (
  userProgress: UserProgressUpdate
): Observable<UserProgressUpdate> => {
  logDebug("user-service", "challengeProgressUpdate", "params", {
    userProgress,
  });
  if (userProgress.gameUpdates.updates.challenges.length > 0) {
    return forkJoin(
      userProgress.gameUpdates.updates.challenges.map((ch) =>
        updateUserChallenge(userProgress.userProfile.userId, ch.challengeId, {
          challengeStatus: ch.status,
        }).pipe(
          tap(() => {
            return getUserChallengeById(
              userProgress.userProfile.userId,
              ch.challengeId
            ).pipe(
              filter(() => ch.status === "FAILED" || ch.status === "COMPLETED"),
              concatMap((userChallenge) =>
                cancelPendingChallengeNotifications(
                  userChallenge.scheduledActions
                )
              )
            );
          })
        )
      )
    ).pipe(
      map(() => {
        const failed = userProgress.gameUpdates.updates.challenges.filter(
          (c) => c.status === "FAILED"
        ).length;
        const completed = userProgress.gameUpdates.updates.challenges.filter(
          (c) => c.status === "COMPLETED"
        ).length;
        const newProgress: UserProgressUpdate = {
          ...userProgress,
          userProfile: {
            ...userProgress.userProfile,
            stats: {
              ...userProgress.userProfile.stats,
              challenges: {
                ...userProgress.userProfile.stats.challenges,
                completed:
                  userProgress.userProfile.stats.challenges.completed +
                  completed,
                failed:
                  userProgress.userProfile.stats.challenges.failed + failed,
                failedStreak: completed ? 0 : failed,
                completedStreak: completed,
              },
            },
          },
          updateSync: true,
        };
        logDebug("user-service", "challengeProgressUpdate", "return", {
          newProgress,
        });
        return newProgress;
      })
    );
  } else {
    logDebug("user-service", "challengeProgressUpdate", "return", {
      userProgress,
    });
    return of(userProgress);
  }
};

const updateUnlockedItems = (
  userProgress: UserProgressUpdate
): UserProgressUpdate => {
  const updateSync =
    Object.values(userProgress.gameUpdates.unlocked).flat().length > 0;

  const updatedProfile: UserProfile = {
    ...userProgress.userProfile,
    unlocked: {
      achievements: [
        ...userProgress.userProfile.unlocked.achievements,
        ...userProgress.gameUpdates.unlocked.achievements,
      ],
      chapters: [
        ...userProgress.userProfile.unlocked.chapters,
        ...userProgress.gameUpdates.unlocked.chapters,
      ],
      locks: [
        ...userProgress.userProfile.unlocked.locks,
        ...userProgress.gameUpdates.unlocked.locks,
      ],
      rewards: [
        ...userProgress.userProfile.unlocked.rewards,
        ...userProgress.gameUpdates.unlocked.rewards,
      ],
    },
  };
  return {
    ...userProgress,
    userProfile: updatedProfile,
    updateSync: userProgress.updateSync || updateSync,
  };
};

function updateUserProgress(
  gameUpdates: GameUpdates,
  userProfile: UserProfile
): Observable<UserProgressUpdate> {
  logDebug("user-service", "updateUserProgress", "params", {
    gameUpdates,
    userProfile,
  });
  const userScore = userScoreUpdate(gameUpdates, userProfile);
  return of(userScore).pipe(
    concatMap((p) => challengeProgressUpdate(p)),
    map((p) => updateUnlockedItems(p)),
    concatMap((userProgressUpdate) => {
      const lastSync = new Date().getTime();
      const update =
        userProgressUpdate.updateSync ||
        userProgressUpdate.gameUpdates.notifications.progress.length > 0;
      if (update) {
        return updateUserProfile(userProgressUpdate.userProfile.userId, {
          ...userProgressUpdate.userProfile,
          lastSync,
        }).pipe(map(() => ({ ...userProgressUpdate, lastSync })));
      } else {
        return of({
          ...userProgressUpdate,
          lastSync: userProfile.lastSync,
        });
      }
    }),
    map((data) => {
      logDebug("user-service", "updateUserProgress", "return", data);
      return data;
    })
  );
}

function getUserChallengeById(userId: string, challengeId: string) {
  return getEntitySubCollectionItem<UserChallenge>(
    PROFILES,
    userId,
    CHALLENGES,
    challengeId
  );
}

function getChallengeNotificationId(challengeId: string, actionDate: number) {
  console.log(`${challengeId},${actionDate}`);
  return Math.round(Math.random() * (999999 - 100000) + 100000);
}

function cancelPendingChallengeNotifications(
  scheduledActions: Array<ScheduledAction>
) {
  const notificationIds = scheduledActions.map((ac) =>
    getChallengeNotificationId(ac.challengeId, ac.actionDate)
  );
  return notificationIds.length > 0
    ? new NotificationsManager().cancelScheduledNotifications(notificationIds)
    : EMPTY;
}

function getUserPastPendingActions(userId: string) {
  return subCollectionObservable<UserChallenge>(
    PROFILES,
    userId,
    CHALLENGES
  ).pipe(
    first(),
    map((challenges) => {
      const nearestMidnight = subDays(
        dateTimeMidnight(new Date().getTime()),
        1
      );
      const pastPendingActions = challenges.map((challenge) => {
        const challengePastActions = challenge.scheduledActions.filter(
          (challengeAction) => {
            const isPast =
              isBefore(new Date(challengeAction.actionDate), nearestMidnight) &&
              challengeAction.actionStatus === "upcoming";
            return isPast;
          }
        );
        return {
          challengeId: challenge.challengeId,
          actions: challengePastActions,
        };
      });
      return pastPendingActions;
    })
  );
}

export {
  userProfileObservable,
  userAuthObservable,
  userLatestUpdatedChallenges,
  getUserChallenge,
  updateUserFeedItemObservable,
  userFeedObservable,
  userChallengeObservable,
  userChallengeListObservable,
  updateUserChallenge,
  updateUserProfile,
  updateActionStatus,
  addUserChallenge,
  generateActions,
  _computeRepeat,
  addUserQuizz,
  updateUserProgress,
  getOrCreateUserProfile,
  getUserProfile,
  getUserChallengeById,
  cancelPendingChallengeNotifications,
  getChallengeNotificationId,
  getUserPastPendingActions,
  bulkUpdateActionStatus,
  deleteUserProfile
};
